import { VoteType, type VoteStats } from "query/graphql"
import {
  getTotalWeight,
  getWeightLabel,
  stringToBigNumber,
} from "common/helpers/bignumber"
import type { GovernorVotesCount } from "proposal/helpers/votes"
import { getQuorumVoteTypeFilter } from "proposal/helpers/votes"

const getSupportAndAgainstVotesCount = (
  voteStats?: VoteStats[] | null,
  isIncludePendingVotes = false,
) => {
  const supportVotesCount =
    voteStats?.find((stat) => stat.type === VoteType.For)?.votesCount ?? "0"
  const againstVotesCount =
    voteStats?.find((stat) => stat.type === VoteType.Against)?.votesCount ?? "0"

  if (isIncludePendingVotes) {
    const pendingSupportVotesCount =
      voteStats?.find((stat) => stat.type === VoteType.Pendingfor)
        ?.votesCount ?? "0"
    const pendingAgainstVotesCount =
      voteStats?.find((stat) => stat.type === VoteType.Pendingagainst)
        ?.votesCount ?? "0"

    return {
      supportVotesCount: stringToBigNumber(supportVotesCount).add(
        stringToBigNumber(pendingSupportVotesCount),
      ),
      againstVotesCount: stringToBigNumber(againstVotesCount).add(
        stringToBigNumber(pendingAgainstVotesCount),
      ),
    }
  }

  return {
    supportVotesCount,
    againstVotesCount,
  }
}

type UseVotesCastResultsCalculationsProps = {
  quorum: string
  decimals: number
  voteStats?: VoteStats[] | null
  governor?: GovernorVotesCount
  isIncludePendingVotes?: boolean
  isVeto?: boolean
}

export const useVotesCastResultsCalculations = ({
  quorum,
  decimals,
  voteStats,
  governor,
  isIncludePendingVotes = false,
  isVeto = false,
}: UseVotesCastResultsCalculationsProps) => {
  const quorumVoteTypeFilter = getQuorumVoteTypeFilter({
    governor,
    isIncludePendingVotes,
  })

  const weights = voteStats
    ? voteStats
        ?.filter((voteStat) => quorumVoteTypeFilter.includes(voteStat.type))
        .map(({ votesCount }) => `${votesCount}`)
    : []
  const totalWeight = getTotalWeight(weights)
  const totalWeightLabel = getWeightLabel(totalWeight, decimals)

  const quorumBigNumber = stringToBigNumber(quorum)
  const quorumLabel = getWeightLabel(quorumBigNumber, decimals)

  const isQuorumMet = totalWeight.gte(quorumBigNumber)

  const { supportVotesCount, againstVotesCount } =
    getSupportAndAgainstVotesCount(voteStats, isIncludePendingVotes)

  const supportVoteWeightBigNumber = stringToBigNumber(`${supportVotesCount}`)
  const againstVoteWeightBigNumber = stringToBigNumber(`${againstVotesCount}`)

  const isMajoritySupport = isVeto
    ? againstVoteWeightBigNumber.gt(supportVoteWeightBigNumber)
    : supportVoteWeightBigNumber.gt(againstVoteWeightBigNumber)

  return {
    isQuorumMet,
    totalWeightLabel,
    quorumLabel,
    isMajoritySupport,
    supportVoteWeightBigNumber,
    againstVoteWeightBigNumber,
  }
}
